import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import API from '../services/api';
import { DEMO_PROFILES } from '../data/demo';
import Button from '../components/ui/Button';
import GlassCard from '../components/ui/GlassCard';
import { toast } from 'react-toastify';
import { FiX, FiStar, FiHeart, FiMapPin, FiCheckCircle, FiZap } from 'react-icons/fi';
import { FaHeart, FaStar } from 'react-icons/fa';

const Discover = () => {
  const [profiles, setProfiles] = useState(DEMO_PROFILES);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showVibe, setShowVibe] = useState(false);
  const [selectedVibe, setSelectedVibe] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [showDetail, setShowDetail] = useState(false);
  const [matchResult, setMatchResult] = useState(null);
  const startX = useRef(0);
  const startY = useRef(0);
  const navigate = useNavigate();

  const profile = profiles[currentIndex];
  const remaining = profiles.length - currentIndex;

  const handleSwipe = async (action) => {
    if (!profile) return;
    if (action === 'like' || action === 'superlike') {
      if (Math.random() > 0.6) {
        setMatchResult(profile);
        setTimeout(() => setMatchResult(null), 3000);
      }
    }
    setCurrentIndex(i => i + 1);
    setDragOffset({ x: 0, y: 0 });
  };

  const handleTouchStart = (e) => {
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    setDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!dragging) return;
    const dx = e.touches[0].clientX - startX.current;
    const dy = e.touches[0].clientY - startY.current;
    setDragOffset({ x: dx, y: dy });
  };

  const handleTouchEnd = () => {
    setDragging(false);
    if (Math.abs(dragOffset.x) > 120) {
      handleSwipe(dragOffset.x > 0 ? 'like' : 'pass');
    } else if (dragOffset.y < -120) {
      setShowDetail(true);
    }
    setDragOffset({ x: 0, y: 0 });
  };

  const getAge = (dob) => {
    if (!dob) return '?';
    const birth = new Date(dob);
    const age = new Date().getFullYear() - birth.getFullYear();
    return age;
  };

  const getSwipeStyle = () => {
    const rotation = dragOffset.x * 0.05;
    const opacity = 1 - Math.abs(dragOffset.x) / 300;
    return {
      transform: `translateX(${dragOffset.x}px) translateY(${dragOffset.y * 0.3}px) rotate(${rotation}deg)`,
      opacity: Math.max(opacity, 0.5),
      transition: dragging ? 'none' : 'all 0.3s ease',
    };
  };

  const getSwipeIndicator = () => {
    if (dragOffset.x > 50) return { text: 'LIKE', color: '#22C55E', opacity: Math.min((dragOffset.x - 50) / 100, 1) };
    if (dragOffset.x < -50) return { text: 'PASS', color: '#EF4444', opacity: Math.min((Math.abs(dragOffset.x) - 50) / 100, 1) };
    if (dragOffset.y < -50) return { text: 'PROFILE', color: '#3B82F6', opacity: Math.min((Math.abs(dragOffset.y) - 50) / 100, 1) };
    return null;
  };

  const indicator = getSwipeIndicator();

  if (remaining === 0) return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#08080C', padding: 32 }}>
      <div style={{ fontSize: 72, marginBottom: 20 }}>🌍</div>
      <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>No more profiles</h2>
      <p style={{ fontSize: 14, color: '#71717A', textAlign: 'center', marginBottom: 24 }}>You've seen everyone nearby. Expand your search or check back later.</p>
      <Button onClick={() => { setCurrentIndex(0); }} variant="secondary" size="lg">Expand Search</Button>
    </div>
  );

  if (!profile) return null;

  return (
    <div style={{ height: '100vh', background: '#08080C', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}
      onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>

      {/* Header */}
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 10 }}>
        <h1 style={{ fontSize: 28, fontWeight: 900, letterSpacing: '-0.02em' }}>
          <span style={{ background: 'linear-gradient(135deg, #FF3D71, #8B5CF6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>VIBE</span>
        </h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowVibe(true)} style={{
            padding: '8px 14px', borderRadius: 12,
            background: selectedVibe ? 'rgba(255,61,113,0.12)' : 'rgba(255,255,255,0.06)',
            border: `1px solid ${selectedVibe ? 'rgba(255,61,113,0.3)' : 'rgba(255,255,255,0.06)'}`,
            color: selectedVibe ? '#FF3D71' : '#A1A1AA', fontSize: 13, fontWeight: 500,
            cursor: 'pointer', fontFamily: 'Inter', display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <FiZap size={14} /> {selectedVibe ? 'VIBE ON' : 'Vibe'}
          </button>
        </div>
      </div>

      {/* Card */}
      <div style={{ flex: 1, padding: '0 16px', position: 'relative' }}>
        <div style={{
          width: '100%', height: '100%', borderRadius: 24, overflow: 'hidden',
          position: 'relative', ...getSwipeStyle(),
        }}>
          {profile.photos?.[0] ? (
            <img src={profile.photos[0]} alt={profile.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${profile.name}&background=1A1A24&color=FF3D71&size=800`; }} />
          ) : (
            <img src={`https://ui-avatars.com/api/?name=${profile.name}&background=1A1A24&color=FF3D71&size=800`} alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          )}

          {/* Gradient overlay */}
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: '60%',
            background: 'linear-gradient(transparent, rgba(0,0,0,0.85))',
          }} />

          {/* Swipe indicator */}
          {indicator && (
            <div style={{
              position: 'absolute', top: 40, left: indicator.color === '#22C55E' ? 20 : 'auto',
              right: indicator.color !== '#22C55E' ? 20 : 'auto',
              opacity: indicator.opacity, transform: `rotate(${indicator.color === '#22C55E' ? -15 : 15}deg)`,
              padding: '8px 20px', borderRadius: 12, border: `3px solid ${indicator.color}`,
              color: indicator.color, fontSize: 24, fontWeight: 800, letterSpacing: '0.05em',
              pointerEvents: 'none',
            }}>{indicator.text}</div>
          )}

          {/* Profile info */}
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '0 20px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <h2 style={{ fontSize: 28, fontWeight: 800 }}>{profile.name}</h2>
              <span style={{ fontSize: 22, color: '#A1A1AA' }}>{getAge(profile.dob)}</span>
              {profile.isVerified && <FiCheckCircle size={18} style={{ color: '#3B82F6' }} />}
              {profile.isPremium && <span style={{ fontSize: 11, padding: '2px 8px', background: 'rgba(255,184,0,0.2)', color: '#F59E0B', borderRadius: 6, fontWeight: 600 }}>PRO</span>}
            </div>
            <p style={{ fontSize: 14, color: '#A1A1AA', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
              <FiMapPin size={13} /> {profile.city} • {profile.distance} km away
            </p>
            <p style={{ fontSize: 14, color: '#D4D4D8', marginBottom: 10, lineHeight: 1.4 }}>{profile.bio}</p>

            {profile.compatibility && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '6px 12px', borderRadius: 10,
                background: 'rgba(255,61,113,0.1)', border: '1px solid rgba(255,61,113,0.2)',
                marginBottom: 10,
              }}>
                <FaHeart size={12} style={{ color: '#FF3D71' }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: '#FF3D71' }}>{profile.compatibility}% Match</span>
              </div>
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {profile.interests?.slice(0, 4).map(i => (
                <span key={i} style={{
                  padding: '5px 10px', borderRadius: 8, fontSize: 12,
                  background: 'rgba(255,255,255,0.08)', color: '#A1A1AA',
                }}>{i}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Stacked cards behind */}
        {profiles[currentIndex + 1] && (
          <div style={{
            position: 'absolute', top: 8, left: 20, right: 20, bottom: -8,
            borderRadius: 24, background: '#111118', border: '1px solid rgba(255,255,255,0.04)',
            zIndex: -1, transform: 'scale(0.95)',
          }} />
        )}
        {profiles[currentIndex + 2] && (
          <div style={{
            position: 'absolute', top: 16, left: 24, right: 24, bottom: -16,
            borderRadius: 24, background: '#111118', border: '1px solid rgba(255,255,255,0.02)',
            zIndex: -2, transform: 'scale(0.9)',
          }} />
        )}
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, padding: '20px 20px 28px' }}>
        <button onClick={() => handleSwipe('pass')} style={{
          width: 56, height: 56, borderRadius: 28, border: '2px solid rgba(239,68,68,0.3)',
          background: 'rgba(239,68,68,0.08)', color: '#EF4444', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s',
        }}><FiX size={24} strokeWidth={2.5} /></button>

        <button onClick={() => handleSwipe('superlike')} style={{
          width: 48, height: 48, borderRadius: 24, border: '2px solid rgba(59,130,246,0.3)',
          background: 'rgba(59,130,246,0.08)', color: '#3B82F6', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s',
        }}><FaStar size={18} /></button>

        <button onClick={() => handleSwipe('like')} style={{
          width: 64, height: 64, borderRadius: 32, border: 'none',
          background: 'linear-gradient(135deg, #FF3D71, #EC4899)',
          color: 'white', cursor: 'pointer', boxShadow: '0 4px 24px rgba(255,61,113,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s',
        }}><FaHeart size={26} /></button>

        <button onClick={() => handleSwipe('superlike')} style={{
          width: 48, height: 48, borderRadius: 24, border: '2px solid rgba(255,184,0,0.3)',
          background: 'rgba(255,184,0,0.08)', color: '#F59E0B', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s',
        }}><FiStar size={18} /></button>

        <button onClick={() => setShowDetail(true)} style={{
          width: 56, height: 56, borderRadius: 28, border: '2px solid rgba(34,197,94,0.3)',
          background: 'rgba(34,197,94,0.08)', color: '#22C55E', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s',
        }}><FiHeart size={22} /></button>
      </div>

      {/* Vibe Modal */}
      {showVibe && (
        <div onClick={() => setShowVibe(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 100,
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            width: '100%', maxWidth: 430, background: '#111118',
            borderRadius: '24px 24px 0 0', padding: '24px 20px 40px',
            animation: 'slideUp 0.3s ease',
          }}>
            <div style={{ width: 40, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.15)', margin: '0 auto 20px' }} />
            <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>What's your vibe today?</h3>
            <p style={{ fontSize: 13, color: '#71717A', marginBottom: 20 }}>Choose a vibe to discover people in that mood</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
              {[
                { id: 'coffee', label: 'Coffee Date', icon: '☕', color: '#F59E0B' },
                { id: 'movie', label: 'Movie Night', icon: '🍿', color: '#8B5CF6' },
                { id: 'travel', label: 'Travel Partner', icon: '✈️', color: '#3B82F6' },
                { id: 'dinner', label: 'Dinner Date', icon: '🍽', color: '#FF3D71' },
                { id: 'talk', label: 'Just Talk', icon: '💬', color: '#22C55E' },
                { id: 'serious', label: 'Serious', icon: '❤️', color: '#EC4899' },
              ].map(v => (
                <button key={v.id} onClick={() => { setSelectedVibe(v.id); setShowVibe(false); }}
                  style={{
                    padding: '16px', borderRadius: 16, border: `1px solid ${selectedVibe === v.id ? v.color + '40' : 'rgba(255,255,255,0.06)'}`,
                    background: selectedVibe === v.id ? v.color + '15' : 'rgba(255,255,255,0.03)',
                    cursor: 'pointer', textAlign: 'center', fontFamily: 'Inter', transition: 'all 0.2s',
                  }}>
                  <div style={{ fontSize: 28, marginBottom: 6 }}>{v.icon}</div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: selectedVibe === v.id ? v.color : '#A1A1AA' }}>{v.label}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Match popup */}
      {matchResult && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 200,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          animation: 'fadeIn 0.3s ease',
        }}>
          {[...Array(20)].map((_, i) => (
            <div key={i} style={{
              position: 'absolute',
              left: `${Math.random() * 100}%`, top: `${40 + Math.random() * 30}%`,
              fontSize: 20 + Math.random() * 20,
              animation: `confetti ${1 + Math.random() * 2}s ease-out forwards`,
              animationDelay: `${Math.random() * 0.5}s`,
            }}>{['❤️', '💕', '✨', '🎉', '💫'][Math.floor(Math.random() * 5)]}</div>
          ))}
          <h1 style={{
            fontSize: 36, fontWeight: 900, marginBottom: 24,
            background: 'linear-gradient(135deg, #FF3D71, #8B5CF6)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            animation: 'scaleIn 0.5s ease',
          }}>IT'S A MATCH!</h1>
          <div style={{ display: 'flex', gap: -10, marginBottom: 16 }}>
            <div style={{ width: 80, height: 80, borderRadius: 40, overflow: 'hidden', border: '3px solid #FF3D71' }}>
              <img src={matchResult.photos?.[0] || `https://ui-avatars.com/api/?name=${matchResult.name}&background=1A1A24&color=FF3D71`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          </div>
          <p style={{ fontSize: 16, color: '#A1A1AA', marginBottom: 32 }}>You and {matchResult.name} liked each other</p>
          <div style={{ display: 'flex', gap: 12 }}>
            <Button onClick={() => setMatchResult(null)} variant="secondary" size="lg">Keep Exploring</Button>
            <Button onClick={() => { setMatchResult(null); navigate('/matches'); }} size="lg">Say Hello</Button>
          </div>
        </div>
      )}

      {/* Profile detail modal */}
      {showDetail && (
        <div style={{
          position: 'fixed', inset: 0, background: '#08080C', zIndex: 100,
          overflowY: 'auto', animation: 'slideUp 0.3s ease',
        }}>
          <div style={{ position: 'sticky', top: 0, padding: '16px 20px', background: '#08080C', zIndex: 10, display: 'flex', justifyContent: 'space-between' }}>
            <button onClick={() => setShowDetail(false)} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 12, width: 40, height: 40, color: '#A1A1AA', cursor: 'pointer' }}>✕</button>
          </div>
          <div style={{ padding: '0 20px 100px' }}>
            <div style={{ height: 400, borderRadius: 20, overflow: 'hidden', marginBottom: 20 }}>
              <img src={profile.photos?.[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${profile.name}&background=1A1A24&color=FF3D71&size=800`; }} />
            </div>
            <h2 style={{ fontSize: 26, fontWeight: 800, marginBottom: 4 }}>{profile.name}, {getAge(profile.dob)}</h2>
            <p style={{ fontSize: 14, color: '#A1A1AA', marginBottom: 16 }}>{profile.profession} at {profile.education}</p>
            <p style={{ fontSize: 15, color: '#D4D4D8', lineHeight: 1.6, marginBottom: 24 }}>{profile.bio}</p>

            {profile.prompts?.map((p, i) => (
              <div key={i} style={{ padding: 16, borderRadius: 16, background: 'rgba(255,61,113,0.06)', border: '1px solid rgba(255,61,113,0.12)', marginBottom: 12 }}>
                <p style={{ fontSize: 13, color: '#FF3D71', fontWeight: 600, marginBottom: 6 }}>{p.q}</p>
                <p style={{ fontSize: 14, color: '#D4D4D8' }}>{p.a}</p>
              </div>
            ))}

            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Interests</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24 }}>
              {profile.interests?.map(i => (
                <span key={i} style={{ padding: '8px 14px', borderRadius: 12, background: 'rgba(255,255,255,0.06)', color: '#A1A1AA', fontSize: 13 }}>{i}</span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Discover;
